import { buildFrame, parseFrame, serializeFrame, type StyxFrame } from "./frame";

export type ConnectionState = "disconnected" | "connecting" | "open" | "closed";

export function defaultWsUrl(): string {
  const env = import.meta.env.VITE_STYX_WS;
  if (env && env.length > 0) return env;
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

type FrameHandler = (frame: StyxFrame) => void;
type StateHandler = (s: ConnectionState) => void;

const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 800;

export class StyxClient {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private readonly frameHandlers = new Set<FrameHandler>();
  private readonly stateHandlers = new Set<StateHandler>();
  private shouldReconnect = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;

  constructor(url: string) {
    this.url = url;
  }

  onFrame(h: FrameHandler): () => void {
    this.frameHandlers.add(h);
    return () => this.frameHandlers.delete(h);
  }

  onState(h: StateHandler): () => void {
    this.stateHandlers.add(h);
    return () => this.stateHandlers.delete(h);
  }

  private emitState(s: ConnectionState) {
    this.stateHandlers.forEach((fn) => fn(s));
  }

  private emitFrame(f: StyxFrame) {
    this.frameHandlers.forEach((fn) => fn(f));
  }

  connect(): void {
    this.shouldReconnect = true;
    this.clearReconnectTimer();
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    this.openSocket();
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect) return;
    const delay = Math.min(INITIAL_BACKOFF_MS * Math.pow(2, this.attempt), MAX_BACKOFF_MS);
    this.attempt += 1;
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  private openSocket() {
    this.emitState("connecting");
    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.emitState("open");
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") return;
      const frame = parseFrame(ev.data);
      if (frame) this.emitFrame(frame);
    };

    ws.onerror = () => {
      /* onclose follows */
    };

    ws.onclose = () => {
      this.ws = null;
      if (this.shouldReconnect) {
        /** Emit before backoff so UI stays out of sustained `closed` (fixes mobile overlay flicker). */
        this.emitState("connecting");
        this.scheduleReconnect();
      } else {
        this.emitState("closed");
      }
    };
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    try {
      this.ws?.close(1000, "client_disconnect");
    } catch {
      /* ignore */
    }
    this.ws = null;
  }

  send(type: string, payload: Record<string, unknown>, id?: string): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    const f = buildFrame(type, payload, id);
    this.ws.send(serializeFrame(f));
    return true;
  }

  get ready(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
